import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "../../utils/axiosConfig";
import { useUser } from "../../context/UserContext";
import "../../styles/features/auth/Register.css";
import { useLocation, useNavigate } from "react-router-dom";

export default function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/profile";

  const { user, setUser, refreshUser } = useUser();

  const [form, setForm] = useState({
    firstname: "",
    surname: "",
    birthDate: "",
    gender: "",
    acceptTerms: false,
    acceptParticipationRules: false,
  });

  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

useEffect(() => {
  let cancelled = false;

  (async () => {
    try {
      let currentUser = user;

      if (!currentUser) {
        currentUser = await refreshUser();
      }

      if (cancelled || !currentUser) return;

      if (currentUser.profileCompleted) {
        navigate(from, { replace: true });
        return;
      }

      setForm({
        firstname: currentUser.firstname || "",
        surname: currentUser.surname || "",
        birthDate: currentUser.birthDate || "",
        gender: currentUser.gender || "",
        acceptTerms: currentUser.acceptTerms || false,
        acceptParticipationRules:
          currentUser.acceptParticipationRules || false,
      });
    } catch (err) {
      console.error("Onboarding load error:", err);

      const status = err?.response?.status;

      const errorMessage =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.response?.data?.error;

      if (
        status === 409 &&
        errorMessage === "ACCOUNT_LINK_REQUIRED"
      ) {
        navigate("/account-link", { replace: true });
        return;
      }

      if (!cancelled) {
        setMessage("Nie udało się pobrać danych profilu.");
      }
    } finally {
      if (!cancelled) {
        setPageLoading(false);
      }
    }
  })();

  return () => {
    cancelled = true;
  };
}, [user, refreshUser, navigate, from]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    const nameRegex = /^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ\s-]{2,}$/;

    if (!nameRegex.test(form.firstname)) {
      return setMessage("Imię musi mieć min. 2 litery i zawierać tylko litery.");
    }
    if (!nameRegex.test(form.surname)) {
      return setMessage("Nazwisko musi mieć min. 2 litery i zawierać tylko litery.");
    }
    if (!form.birthDate) return setMessage("Podaj datę urodzenia.");
    if (!form.gender) return setMessage("Wybierz płeć.");
    if (!form.acceptTerms) return setMessage("Musisz zaakceptować politykę prywatności.");
    if (!form.acceptParticipationRules) return setMessage("Musisz zaakceptować warunki udziału.");

    try {
      setSubmitting(true);

      const payload = {
        firstname: form.firstname.trim(),
        surname: form.surname.trim(),
        birthDate: form.birthDate,
        gender: form.gender,
        acceptTerms: form.acceptTerms,
        acceptParticipationRules: form.acceptParticipationRules,
      };

      const response = await axios.put("/me", payload);

      setUser(response.data);
      localStorage.setItem("user", JSON.stringify(response.data));

      navigate(from, { replace: true });
    } catch (err) {
      console.error("PUT /me error:", err);

      setMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Nie udało się zapisać danych. Spróbuj ponownie."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (pageLoading) {
    return (
      <section className="auth-page">
        <div className="auth-container">
          <h2>Ładowanie danych…</h2>
          <p>Jeszcze chwila.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="auth-page">
      <div className="auth-container">
        <h2>Uzupełnij dane</h2>

        <form onSubmit={handleSubmit} className="register-form">
          <input
            name="firstname"
            placeholder="Imię"
            value={form.firstname}
            onChange={handleChange}
            required
          />

          <input
            name="surname"
            placeholder="Nazwisko"
            value={form.surname}
            onChange={handleChange}
            required
          />

          <div className="field-group">
            <label htmlFor="birthDate" className="field-label">
              Data urodzenia
            </label>
            <input
              id="birthDate"
              name="birthDate"
              type="date"
              value={form.birthDate}
              onChange={handleChange}
              required
            />
          </div>

          <select name="gender" value={form.gender} onChange={handleChange} required>
            <option value="" disabled>
              Wybierz płeć
            </option>
            <option value="FEMALE">Kobieta</option>
            <option value="MALE">Mężczyzna</option>
            <option value="OTHER">Inna</option>
          </select>

          <button type="submit" disabled={submitting}>
            {submitting ? "Zapisywanie…" : "Zapisz i przejdź dalej"}
          </button>

          <div className="guestSignup__section">
            <label className="check">
              <input
                type="checkbox"
                name="acceptTerms"
                checked={form.acceptTerms}
                onChange={handleChange}
              />
              Akceptuję{" "}
              <a href="/privacy-policy" target="_blank" rel="noreferrer">
                politykę prywatności
              </a>
            </label>

            <label className="check">
              <input
                type="checkbox"
                name="acceptParticipationRules"
                checked={form.acceptParticipationRules}
                onChange={handleChange}
              />
              Zapoznałem/-am się z{" "}
              <a href="/terms" target="_blank" rel="noreferrer">
                warunkami udziału
              </a>
            </label>
          </div>
        </form>

        <p aria-live="polite">{message}</p>
      </div>
    </section>
  );
}
